import type { Encounter, Reference } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import type * as appointments from '@seen/core/models/appointments/index'
import type { ExtendedComposition } from '@seen/core/models/compositions/composition'
import { AppointmentEncounter } from '@seen/core/models/encounters/appointment-encounter'
import { ExtendedEncounter } from '@seen/core/models/encounters/encounter'
import type { Participant } from '@seen/core/models/patients/participant'
import { useTransferEncounterData } from '#src/components/rich-text-editor/EncounterNode/useTransferEncounterData.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { readOptions } from '#src/query-options/readOptions.ts'
import { searchOptions } from '#src/query-options/searchOptions.ts'

export function useLinkAppointment({
  composition,
  participant,
}: {
  composition: ExtendedComposition | undefined
  participant: Participant | null
}) {
  const transaction = useTransactionMutation()
  const transferEncounterData = useTransferEncounterData()

  async function linkAppointment(
    value: appointments.ExtendedAppointment,
    existingEncounter?: AppointmentEncounter
  ) {
    invariant(composition, 'Composition is required')
    invariant(participant, 'Participant is required')

    await transaction.mutateAsync(async (txn, { queryClient, medplum }) => {
      let encounter = existingEncounter
      if (!encounter) {
        const encounters = await queryClient.fetchQuery(
          searchOptions(medplum, ExtendedEncounter, {
            appointment: value.reference,
          })
        )
        const found = encounters.at(0)
        if (found) {
          encounter = found as AppointmentEncounter
        }
      }

      let encounterRef: Reference<Encounter>

      if (encounter) {
        encounterRef = encounter.reference

        if (composition.encounter) {
          await transferEncounterData({
            txn,
            oldEncounterRef: composition.encounter,
            newEncounterRef: encounterRef,
            existingNewEncounter: encounter,
            patientRef: participant.reference,
          })
        }
      } else {
        const newEncounter = new AppointmentEncounter({
          appointment: value.reference,
          patient: participant.reference,
          practitioner: value.practitioners[0],
          startTime: value.start,
          endTime: value.end,
        })

        let prefetchedOldEncounter: ExtendedEncounter | undefined
        if (composition.encounter) {
          prefetchedOldEncounter = await queryClient.fetchQuery(
            readOptions(medplum, ExtendedEncounter, composition.encounter)
          )
          if (prefetchedOldEncounter) {
            newEncounter.diagnosis = prefetchedOldEncounter.diagnosis
          }
        }

        encounterRef = txn.create(newEncounter)

        if (composition.encounter) {
          await transferEncounterData({
            txn,
            oldEncounterRef: composition.encounter,
            newEncounterRef: encounterRef,
            patientRef: participant.reference,
            prefetchedOldEncounter,
          })
        }
      }

      txn.patch(composition, (c) => {
        c.encounter = encounterRef
        if (value.startDateOnly) {
          c.event = [
            {
              period: {
                start: value.startDateOnly,
                end: value.startDateOnly,
              },
            },
          ]
        }
      })
    })
  }

  return { linkAppointment, isPending: transaction.isPending }
}
